import { useEffect, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  CREATE_REACTION_QUESTION,
  UPDATE_REACTION_QUESTION,
  GET_REACTION_QUESTION,
  GET_QUESTION_INTERESTS,
  GET_INTERESTS_FOR_ASSIGNMENT,
  ASSIGN_INTEREST,
  REMOVE_INTEREST,
} from "../Queries/GifviewContentQueries";
import { editGifviewContent, setView, Views } from "../store/session";
import { useAppDispatch, useAppSelector } from "../store/index";

interface Interest {
  id: string;
  name_en: string;
  depth: string;
  parent_id: string | null;
  color: string | null;
}

interface PostInterest {
  id: string;
  interest_id: string;
  interest: Interest;
}

export function EditGifviewContent() {
  const dispatch = useAppDispatch();
  const questionId = useAppSelector(
    (state) => state.session.editGifviewContent
  );
  const isEditing = !!questionId;

  const [title, setTitle] = useState("");
  const [published, setPublished] = useState(false);
  const [publishedDate, setPublishedDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [selectedInterestId, setSelectedInterestId] = useState("");

  // Analytics state (only for existing published questions)
  const [reactionsCount, setReactionsCount] = useState(0);
  const [uniqueReactors, setUniqueReactors] = useState(0);
  const [viewsCount, setViewsCount] = useState(0);

  // Queries
  const { data: interestsData } = useQuery(GET_INTERESTS_FOR_ASSIGNMENT);
  const [fetchQuestionInterests, { data: questionInterestsData }] =
    useLazyQuery(GET_QUESTION_INTERESTS, { fetchPolicy: "network-only" });

  // Load existing question data for editing
  const [fetchQuestion] = useLazyQuery(GET_REACTION_QUESTION, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const question = data?.posts_by_pk;
      if (question) {
        setTitle(question.title || "");
        setPublished(question.published);
        setPublishedDate(question.published_date);
        setReactionsCount(
          question.comments_aggregate?.aggregate?.count ?? 0
        );
        const userIds = new Set(
          (question.comments_aggregate?.nodes ?? []).map(
            (n: { user_id: string }) => n.user_id
          )
        );
        setUniqueReactors(userIds.size);
        setViewsCount(
          question.post_views_aggregate?.aggregate?.count ?? 0
        );
      }
    },
  });

  // Mutations
  const [createQuestion] = useMutation(CREATE_REACTION_QUESTION);
  const [updateQuestion] = useMutation(UPDATE_REACTION_QUESTION);
  const [assignInterest] = useMutation(ASSIGN_INTEREST);
  const [removeInterest] = useMutation(REMOVE_INTEREST);

  useEffect(() => {
    if (isEditing && questionId) {
      fetchQuestion({ variables: { id: questionId } });
      fetchQuestionInterests({ variables: { postId: questionId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId]);

  const allInterests: Interest[] = interestsData?.interests ?? [];
  const assignedInterests: PostInterest[] =
    questionInterestsData?.post2interest ?? [];
  const assignedInterestIds = new Set(
    assignedInterests.map((pi) => pi.interest_id)
  );
  const availableInterests = allInterests.filter(
    (i) => !assignedInterestIds.has(i.id)
  );

  const handleSave = async () => {
    setError(null);
    setSuccessMsg(null);

    if (!title.trim()) {
      setError("Question text is required.");
      return;
    }

    try {
      if (isEditing) {
        const setInput: Record<string, unknown> = { title: title.trim() };
        if (published && !publishedDate) {
          setInput.published = true;
          setInput.published_date = new Date().toISOString();
        } else {
          setInput.published = published;
          if (!published) {
            setInput.published_date = null;
          }
        }
        await updateQuestion({
          variables: { id: questionId, set: setInput },
        });
        setSuccessMsg("Question updated.");
      } else {
        const now = new Date().toISOString();
        const result = await createQuestion({
          variables: {
            title: title.trim(),
            published,
            published_date: published ? now : null,
            publishing_date: published ? now : null,
            created_date: now,
          },
        });
        const newId = result.data?.insert_posts_one?.id;
        if (newId) {
          setSuccessMsg("Question created. You can now assign interests.");
          // Reload as edit mode with the new ID
          dispatch(editGifviewContent(newId));
        }
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "An error occurred.");
    }
  };

  const handlePublishToggle = async (checked: boolean) => {
    setPublished(checked);
    if (isEditing) {
      try {
        const setInput: Record<string, unknown> = { published: checked };
        if (checked && !publishedDate) {
          const now = new Date().toISOString();
          setInput.published_date = now;
          setInput.publishing_date = now;
          setPublishedDate(now);
        }
        if (!checked) {
          setInput.published_date = null;
          setInput.publishing_date = null;
          setPublishedDate(null);
        }
        await updateQuestion({
          variables: { id: questionId, set: setInput },
        });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Failed to update.");
      }
    }
  };

  const handleAssignInterest = async () => {
    if (!selectedInterestId || !questionId) return;
    try {
      await assignInterest({
        variables: { postId: questionId, interestId: selectedInterestId },
      });
      setSelectedInterestId("");
      fetchQuestionInterests({ variables: { postId: questionId } });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to assign interest.");
    }
  };

  const handleRemoveInterest = async (postInterestId: string) => {
    try {
      await removeInterest({ variables: { id: postInterestId } });
      fetchQuestionInterests({ variables: { postId: questionId } });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to remove interest.");
    }
  };

  const reactionRate =
    viewsCount > 0 ? `${Math.round((uniqueReactors / viewsCount) * 100)}%` : "—";

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => dispatch(setView(Views.GifviewContent))}
        sx={{ mb: 2 }}
      >
        Back to Gifview Content
      </Button>

      <Typography variant="h5" sx={{ mb: 3 }}>
        {isEditing ? "Edit Question" : "Create Question"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}

      {/* Question Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <TextField
          label="Question Text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          placeholder="What's your reaction when Monday hits?"
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={published}
                onChange={(e) => handlePublishToggle(e.target.checked)}
              />
            }
            label="Publish"
          />
          <Chip
            label={published ? "Published" : "Draft"}
            color={published ? "success" : "default"}
            size="small"
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={handleSave}>
            {isEditing ? "Update" : "Create"}
          </Button>
        </Box>
      </Paper>

      {/* Interest Assignment (only when editing) */}
      {isEditing && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Interests
          </Typography>

          {assignedInterests.length > 0 && (
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Interest</TableCell>
                    <TableCell align="center">Remove</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignedInterests.map((pi) => (
                    <TableRow key={pi.id}>
                      <TableCell>
                        <Chip
                          label={pi.interest.name_en}
                          size="small"
                          sx={{
                            backgroundColor: pi.interest.color || undefined,
                            color: pi.interest.color ? "#fff" : undefined,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveInterest(pi.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ display: "flex", gap: 1 }}>
            <Select
              value={selectedInterestId}
              onChange={(e) => setSelectedInterestId(e.target.value)}
              displayEmpty
              size="small"
              sx={{ minWidth: 250 }}
            >
              <MenuItem value="" disabled>
                Select an interest
              </MenuItem>
              {availableInterests.map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  {i.name_en}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="outlined"
              onClick={handleAssignInterest}
              disabled={!selectedInterestId}
            >
              Add
            </Button>
          </Box>
        </Paper>
      )}

      {/* Analytics Card (only for published, editing) */}
      {isEditing && published && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Analytics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  GIF Reactions
                </Typography>
                <Typography variant="h4">{reactionsCount}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Unique Reactors
                </Typography>
                <Typography variant="h4">{uniqueReactors}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Views
                </Typography>
                <Typography variant="h4">{viewsCount}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Reaction Rate
                </Typography>
                <Typography variant="h4">{reactionRate}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
